import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import PractitionerMultiSelect from '../features/users/PractitionerMultiSelect';
import userService from '../services/userService';
import doctorService from '../services/doctorService';
import { useT } from '../i18n/LanguageContext';

// Password validation: 12+ chars with upper, lower, number, and special
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{12,}$/;
function validatePassword(pwd) {
  if (!pwd) return 'Password is required';
  if (pwd.length < 12) return 'Password must be at least 12 characters';
  if (!PASSWORD_REGEX.test(pwd))
    return 'Password must include uppercase, lowercase, number, and special character (@$!%*?&#)';
  return null;
}

// Accept MM/DD/YYYY or ISO; return ISO for backend. Return null if empty or invalid.
function parseDob(dob) {
  if (!dob || !dob.trim()) return null;
  const trimmed = dob.trim();
  // MM/DD/YYYY
  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, mm, dd, yyyy] = slashMatch;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    if (!isNaN(d.getTime())) return d.toISOString();
  }
  // Fallback to native parse (ISO, etc.)
  const d = new Date(trimmed);
  if (!isNaN(d.getTime())) return d.toISOString();
  return 'invalid';
}

function formatDobForDisplay(isoOrDate) {
  if (!isoOrDate) return '';
  const d = new Date(isoOrDate);
  if (isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

/**
 * The roles this form can actually provision.
 *
 * NOT the whole enum. A `clinic` login must be bound to exactly one clinic AT CREATION - the API
 * refuses one without a clinic id - and this form has no clinic field, so offering it would be a
 * picker whose only outcome is a 400. `patient` is not staff-provisioned at all. An option that
 * cannot succeed is worse than an absent one: it reads as a capability.
 */
const CREATABLE_ROLES = [
  {
    value: 'sales_rep',
    label: 'Sales Representative',
    hint: 'Sees only their own clinics, orders and invoices.',
  },
  {
    value: 'admin',
    label: 'Admin',
    hint: 'Every screen, including invoicing, statements and user management.',
  },
];

const ROLE_LABEL = {
  admin: 'Admin',
  sales_rep: 'Sales Representative',
  clinic: 'Clinic',
  patient: 'Patient',
};

export default function RegisterUsers() {
  const t = useT();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    dob: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    licenseNumber: '',
    // Unchanged default. This form only ever made sales reps, and someone opening it to add a rep
    // should not have to choose the thing it already did.
    role: 'sales_rep',
    isActive: true,
  });
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    doctorService
      .getDoctors()
      .then((res) => {
        const list = res.data?.data?.data || res.data?.data?.doctors || res.data?.data || [];
        setDoctors(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, []);

  // Load existing user in edit mode
  useEffect(() => {
    if (!isEdit) return;
    let active = true;
    (async () => {
      try {
        const res = await userService.getUserById(editId);
        const u = res.data?.data || res.data;
        if (!active || !u) return;
        setForm({
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          email: u.email || '',
          password: '',
          dob: formatDobForDisplay(u.dateOfBirth),
          gender: u.gender || '',
          address: u.address?.street || '',
          city: u.address?.city || '',
          state: u.address?.state || '',
          zipCode: u.address?.zipCode || '',
          phone: u.phone || '',
          licenseNumber: u.licenseNumber || '',
          role: u.role || 'sales_rep',
          isActive: u.isActive !== false,
        });
        setSelectedDoctors(Array.isArray(u.assignedDoctors) ? u.assignedDoctors.map(String) : []);
      } catch (err) {
        setError('Failed to load user');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [editId, isEdit]);

  const isRep = form.role === 'sales_rep';
  const roleLabel = t(ROLE_LABEL[form.role] || form.role);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async () => {
    // Required fields
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError('First name, last name, and email are required');
      return;
    }

    // Password: required on create, optional on edit (if provided must meet policy)
    if (!isEdit) {
      const pwdErr = validatePassword(form.password);
      if (pwdErr) {
        setError(pwdErr);
        return;
      }
    } else if (form.password) {
      const pwdErr = validatePassword(form.password);
      if (pwdErr) {
        setError(pwdErr);
        return;
      }
    }

    // DOB
    const dobIso = parseDob(form.dob);
    if (dobIso === 'invalid') {
      setError('Date of Birth must be in MM/DD/YYYY format');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const basePayload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone,
        address: form.address,
        city: form.city,
        state: form.state,
        zipCode: form.zipCode,
        gender: form.gender,
      };
      // A licence number and a provider list describe a SALES REP. Sending them on an admin puts
      // fields on the record that nothing reads and that the Users page cannot explain.
      if (isRep) basePayload.licenseNumber = form.licenseNumber;
      if (dobIso) basePayload.dob = dobIso;

      if (isEdit) {
        // NO `role` HERE, deliberately. The update endpoint does not accept one, so a picker in edit
        // mode would silently do nothing - and a control that appears to grant admin and does not is
        // worse than no control. Changing someone's role stays a decision made where the roles are
        // listed, not a side effect of correcting a phone number.
        const payload = { ...basePayload, isActive: form.isActive };
        if (form.password) payload.password = form.password;
        if (isRep) payload.assignedDoctors = selectedDoctors;
        await userService.updateUser(editId, payload);
        setSuccess('User updated successfully!');
        setTimeout(() => navigate(isRep ? '/sales-reps' : '/users'), 900);
      } else {
        const payload = {
          ...basePayload,
          password: form.password,
          role: form.role,
        };
        if (isRep && selectedDoctors.length > 0) payload.assignedDoctors = selectedDoctors;
        await userService.createUser(payload);
        setSuccess(
          t(
            '{role} created successfully! Share these credentials with them: Email — {email} / Password — {password}',
            {
              role: roleLabel,
              email: form.email.trim(),
              password: form.password,
            }
          )
        );
        setForm({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          dob: '',
          gender: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          phone: '',
          licenseNumber: '',
          // The ROLE survives the reset. Adding three reps in a row is the reason this page exists,
          // and re-picking it each time is how the fourth one silently becomes an admin.
          role: form.role,
          isActive: true,
        });
        setSelectedDoctors([]);
      }
    } catch (err) {
      setError('Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-60">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0089ff]" />
      </div>
    );
  }

  const requiredStar = <span className="text-[#f23e41]">*</span>;

  return (
    <div>
      <PageHeader
        title={
          isEdit
            ? form.role === 'clinic'
              ? t('Edit user account')
              : t('Edit {role}', { role: roleLabel })
            : t('Register {role}', { role: roleLabel })
        }
        subtitle={
          isEdit
            ? t('Update login details or reset the password for this user.')
            : t('Add a new {role} to the system.', { role: roleLabel.toLowerCase() })
        }
      />

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {t(error)}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          {t(success)}
        </div>
      )}

      <div className="space-y-5">
        {/* FIRST on the form, because it decides what the rest of it asks for - and because a role
            chosen last is a role chosen after the reader has stopped paying attention. */}
        <div>
          <label htmlFor="role" className="block text-xs font-medium text-[#64748b] mb-2">
            {t('Role')} {!isEdit && requiredStar}
          </label>
          {isEdit ? (
            /* READ-ONLY IN EDIT MODE. The update endpoint accepts no role, so a live picker here
               would appear to grant admin and quietly do nothing. */
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center h-[50px] px-4 border border-[#e2e8f0] bg-[#f8fafc] rounded-[8px] text-sm text-[#24315d]">
                {roleLabel}
              </span>
              <span className="text-xs text-[#64748b]">
                {t('A role cannot be changed here. Create a new login instead.')}
              </span>
            </div>
          ) : (
            <>
              <select
                id="role"
                value={form.role}
                onChange={(e) => handleChange('role', e.target.value)}
                className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
              >
                {CREATABLE_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {t(r.label)}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-[#64748b]">
                {t(CREATABLE_ROLES.find((r) => r.value === form.role)?.hint)}
              </p>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-2">
              {t('First Name')} {requiredStar}
            </label>
            <input
              type="text"
              value={form.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              placeholder={t('Enter first name')}
              className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-2">
              {t('Last Name')} {requiredStar}
            </label>
            <input
              type="text"
              value={form.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              placeholder={t('Enter last name')}
              className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-2">
              {t('Email')} {requiredStar}
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder={t('Enter email')}
              className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-2">
              {t('Password')} {!isEdit && requiredStar}
              {isEdit && (
                <span className="text-[#94a3b8] font-normal">
                  {' '}
                  {t('(leave blank to keep existing)')}
                </span>
              )}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => handleChange('password', e.target.value)}
              placeholder={isEdit ? t('New password (optional)') : t('Min 12 chars, A-a-1-!')}
              className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
            />
            <p className="text-[11px] text-[#94a3b8] mt-1">
              {t('12+ chars with uppercase, lowercase, number & special character (@$!%*?&#)')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-2">
              {t('Date of Birth')}
            </label>
            <input
              type="date"
              value={(() => {
                // form.dob is stored as MM/DD/YYYY. The native date input needs YYYY-MM-DD.
                const m = (form.dob || '').match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                if (!m) return '';
                const [, mm, dd, yyyy] = m;
                return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
              })()}
              onChange={(e) => {
                const iso = e.target.value; // YYYY-MM-DD
                if (!iso) {
                  handleChange('dob', '');
                  return;
                }
                const [yyyy, mm, dd] = iso.split('-');
                handleChange('dob', `${mm}/${dd}/${yyyy}`);
              }}
              max={new Date().toISOString().slice(0, 10)}
              className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-2">{t('Gender')}</label>
            <div className="relative">
              <select
                value={form.gender}
                onChange={(e) => handleChange('gender', e.target.value)}
                className="w-full h-[50px] px-4 pr-10 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] appearance-none focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff] bg-white"
              >
                <option value="">{t('Select gender')}</option>
                <option value="male">{t('Male')}</option>
                <option value="female">{t('Female')}</option>
                <option value="other">{t('Other')}</option>
              </select>
              <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] text-[20px] pointer-events-none">
                expand_more
              </span>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#64748b] mb-2">{t('Address')}</label>
          <input
            type="text"
            value={form.address}
            onChange={(e) => handleChange('address', e.target.value)}
            placeholder={t('Enter address')}
            className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-2">{t('City')}</label>
            <input
              type="text"
              value={form.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder={t('Enter city')}
              className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-2">{t('State')}</label>
            <input
              type="text"
              value={form.state}
              onChange={(e) => handleChange('state', e.target.value)}
              placeholder={t('Enter state')}
              className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-2">{t('Zip Code')}</label>
            <input
              type="text"
              value={form.zipCode}
              onChange={(e) => handleChange('zipCode', e.target.value)}
              placeholder={t('Enter zip code')}
              className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-2">
              {t('Mobile Number')}
            </label>
            <input
              type="text"
              value={form.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder={t('Enter mobile number')}
              className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
            />
          </div>
        </div>

        {/* A licence number and a provider list describe a SALES REP. Asked of an admin they are
            two fields nobody can answer, on the form that grants the most access in the product. */}
        {isRep && (
          <>
            <div>
              <label className="block text-xs font-medium text-[#64748b] mb-2">
                {t('License Number')}
              </label>
              <input
                type="text"
                value={form.licenseNumber}
                onChange={(e) => handleChange('licenseNumber', e.target.value)}
                placeholder={t('Enter license number')}
                className="w-full h-[50px] px-4 border border-[#d6dce8] rounded-[8px] text-sm text-[#24315d] placeholder:text-[#24315d] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-[#64748b] mb-2">
                {t('Assigned Providers')}
              </label>
              <PractitionerMultiSelect
                doctors={doctors}
                selectedIds={selectedDoctors}
                onChange={setSelectedDoctors}
              />
            </div>
          </>
        )}

        {isEdit && (
          <div className="flex items-center gap-3">
            <input
              id="isActive"
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              className="w-4 h-4 rounded border-[#d6dce8] text-[#0089ff] focus:ring-[#0089ff]"
            />
            <label
              htmlFor="isActive"
              className="text-sm font-medium text-[#24315d] select-none cursor-pointer"
            >
              {t('Active')}
            </label>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 mt-10">
        <button
          onClick={() => navigate(isRep ? '/sales-reps' : '/users')}
          className="h-[50px] w-[110px] border border-[#e2e8f0] rounded-[10px] text-sm font-semibold text-[#97a3b6] hover:bg-gray-50 transition-colors shadow-sm"
        >
          {t('Cancel')}
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="h-[50px] w-[110px] bg-[#0089ff] text-white text-sm font-semibold rounded-[10px] hover:bg-[#0077e6] transition-colors disabled:opacity-50"
        >
          {saving ? t('Saving...') : t('Save')}
        </button>
      </div>
    </div>
  );
}
